// React imports
import React, { useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

// Hooks for Table
import { useTable, usePagination, useSortBy } from 'react-table';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

// Blueprint Imports
import { Box, Checkbox, Grid, Button, TextInput, Modal } from '@blueprint/ui';
import CustomerLogo from '../../CustomerLogo/CustomerLogo';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

// Imports for Pagination Component (Blueprint)
import { Pagination, AmountSelector, PageSelector, NextPreviousBtn, Typography, ContextualNotification, InlineNotification } from '@blueprint/ui';

// Imports for Blueprint Table
import { Table, TableBodyCell, TableHeadCell, TableBodyRow, TableHeadRow, TableHead, TableBody, TSortLabel } from '@blueprint/table';

import { axiosInstance } from '../../../functions/axios';
import amountOptions from '../amountOptions';
import { setCustIdTnxArray, showDeleteLink } from '../../../actions/index';
import { setCaptchaHeader } from '../../../actions/index';
import { NativeCheckbox } from '../../NativeCheckbox/NativeCheckbox';

import { InlineNotificationComponent } from '../../Notification/InlineNotificationComponent';

const StyledGrid = styled(Grid)`
    padding-top: 100px;
    padding-right: 5vw;
    padding-left: 5vw;
    padding-bottom: 5em;
`;

const GridWithScroll = styled(Grid)`
    overflow-x: auto;
    -ms-overflow-style: none;
    padding-bottom: 1em;
`;

const TypographyReason = styled(Typography)`
    &:after {
    }
`;

const PaymentsListTable = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const history = useHistory();
    const [cursor, setCursor] = useState('default');
    const [paymentsListData, setPaymentsListData] = useState([]);
    const [numberOfRecords, setNumberOfRecords] = useState(0);
    const [showCheckboxes, setShowCheckboxes] = useState(true);
    const [showMultipleSelectionsError, setShowMultipleSelectionsError] = useState(false);
    const [multipleSelectionsError, setMultipleSelectionsError] = useState(false);
    const [cancellationReason, setCancellationReason] = useState(false);
    const [showCancellationReasonError, setShowCancellationReasonError] = useState(false);
    const [cancellationReasonError, setCancellationReasonError] = useState(false);
    const [open, setOpen] = useState(false);
    const [fetchCallCount, setFetchCallCount] = useState(1);
    const globalState = useSelector((state) => state);
    const isRegisteredUser = useSelector((state) => state.beneDetails.registeredUser);
    const footerHeight = globalState.beneDetails.footerHeight;
    const ACCOUNT_HISTORY = process.env.REACT_APP_ACCOUNT_HISTORY;
    const PAYMENT_HISTORY = process.env.REACT_APP_PAYMENT_HISTORY;
    const PAYMENT_LIST_URL = process.env.REACT_APP_PAYMENT_LIST_URL;
    const VALIDATE_MULTIPLE_SELECTION_URL = process.env.REACT_APP_VALIDATE_MULTIPLE_SELECTION_URL;
    const CANCEL_PAYMENT_URL = process.env.REACT_APP_CANCEL_PAYMENT_URL;

    useEffect(() => {
        dispatch(setCaptchaHeader(null));
    }, [dispatch]);

    useEffect(() => {
        if (!globalState.beneDetails.details.isLogged) {
            history.push('/');
        }
    }, [globalState, history]);

    const data = useMemo(() => paymentsListData, [paymentsListData]);
    const columns = useMemo(() => {
        const tableAccessorArray = [
            {
                accessor: 'customerName',
            },
            {
                accessor: 'instructedCurrency',
            },
            {
                accessor: 'instructedAmount',
            },
            {
                accessor: 'custBeneInfo',
            },
            {
                accessor: 'status',
            },
        ];

        const paymentListColumns = tableAccessorArray.map((column) => {
            let columnDisplayText = t(`userTable.${column.accessor}`);
            return { ...column, Header: columnDisplayText };
        });
        return paymentListColumns;
    }, [t]);

    const {
        previousPage,
        nextPage,
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        setPageSize,
        gotoPage,
        state: { pageIndex, pageSize },
    } = useTable({ columns, data, ...{ disableSortRemove: true } }, useSortBy, usePagination);

    useEffect(() => {
        const fetchDataOnLoading = async () => {
            try {
                const response = await axiosInstance({
                    method: 'get',
                    url: PAYMENT_LIST_URL,
                });
                const responseJSON = response.data;
                console.log(`Logging ${PAYMENT_LIST_URL} response`, responseJSON);

                const dataCurrencyFormatted = responseJSON.map((item) => {
                    const container = {
                        ...item,
                        instructedAmount: item.instructedAmount,
                        status: item.status.charAt(0) + item.status.slice(1).toLowerCase(),
                    };
                    return container;
                });

                if (globalState.beneDetails.registeredUser && responseJSON.length === 0) {
                    console.log('dispatching allowdelete');
                    dispatch(showDeleteLink(true));
                } else {
                    dispatch(showDeleteLink(false));
                    console.log('not dispatching allowdelete');
                }

                console.log('Payments List Data from API is', dataCurrencyFormatted);

                setPaymentsListData(dataCurrencyFormatted);
                setNumberOfRecords(dataCurrencyFormatted.length);
            } catch (error) {
                console.error(`Logging ${PAYMENT_LIST_URL} error`, error.response);
            }
        };
        fetchDataOnLoading();

        setPageSize(10);
        gotoPage(0); //related defaultCurrentPage 1
    }, [gotoPage, setPageSize, fetchCallCount, PAYMENT_LIST_URL]);

    const [selectedRow, updateRow] = useState([]);
    const handleClick = (id: string): void => {
        console.log('ID of row is ', id);

        const selectedIdIndex = selectedRow.indexOf(id);
        if (selectedIdIndex !== -1) {
            updateRow(selectedRow.filter((item) => item !== id));
        } else {
            updateRow([...selectedRow, id]);
            setShowMultipleSelectionsError(false);
            setMultipleSelectionsError('');
        }
    };

    const [showPaymentCancellation, setShowPaymentCancellation] = useState(false);
    useEffect(() => {
        console.log('Selected Row Array is', selectedRow);
        let showCancel = true;

        if (selectedRow.length === 0) {
            showCancel = false;
        }
        for (let i = 0; i < selectedRow.length; i++) {
            if (paymentsListData[selectedRow[i]].status !== 'Processing') {
                showCancel = false;
            }
        }
        showCancel ? setShowPaymentCancellation(true) : setShowPaymentCancellation(false);
    }, [paymentsListData, selectedRow]);

    const AmountComponent = ({ children, count, prefixText: PrefixText, suffixText: SuffixText }: any) => (
        <>
            <PrefixText>Show</PrefixText>
            {children}
            <SuffixText>of {count} payments</SuffixText>
        </>
    );

    const handleSingleRowClick = (cell) => {
        let custId = cell.row.original.custId;
        console.log(custId);

        let tnxArray = [];
        tnxArray.push(cell.row.original.tnxId);
        console.log(tnxArray);

        if (cell.row.original.status === 'Processing') {
            if (selectedRow.length === 1) {
                updateRow([]);
            }
            if (selectedRow.length === 0) {
                updateRow([...selectedRow, cell.row.id]);
            }
        }
        if (cell.row.original.status === 'Pending' || cell.row.original.status === 'Hold') {
            if (cell.row.original.transactionType === 'FIXED_CREDIT') {
                validateSelections(tnxArray, custId);
            } else {
                let paymentDetails = {
                    custId: custId,
                    tnxIds: tnxArray,
                };

                try {
                    dispatch(setCustIdTnxArray(paymentDetails));
                } catch (e) {
                    console.log('Error dispatching setCustIdTnxArray(paymentDetails)', e);
                }
                history.push({
                    pathname: '/paymentBankDetails',
                    state: {
                        custId: custId,
                        tnxIds: tnxArray,
                    },
                });
            }
        }
    };

    const validateSelections = async (tnxArray, custId) => {
        try {
            const response = await axiosInstance.post(
                VALIDATE_MULTIPLE_SELECTION_URL,
                JSON.stringify({
                    transactionIds: tnxArray,
                })
            );

            if (response.status === 200) {
                let paymentDetails = {
                    custId: custId,
                    tnxIds: tnxArray,
                };

                try {
                    dispatch(setCustIdTnxArray(paymentDetails));
                } catch (e) {
                    console.log('Error dispatching setCustIdTnxArray(paymentDetails)', e);
                }
                history.push({
                    pathname: '/paymentBankDetails',
                    state: {
                        custId: custId,
                        tnxIds: tnxArray,
                    },
                });
            }
        } catch (error) {
            if (error.response.data.message !== null && error.response.data.message !== '') {
                setShowMultipleSelectionsError(true);
                setMultipleSelectionsError(error.response.data.message);
                moveFocusToContextualError();
            }
        }
    };

    const handleMultipleRowSelect = () => {
        let invalidSelections = false;
        let tnxArray = [];
        let custId = 0;
        if (selectedRow.length > 0) {
            for (let i = 0; i < selectedRow.length; i++) {
                const rowIndex = selectedRow[i];

                // if payment status is not pending then mark as invalidSelections
                if (paymentsListData[rowIndex].status !== 'Pending' && paymentsListData[rowIndex].status !== 'Hold') {
                    invalidSelections = true;
                    console.log('Invalid selections is now true', invalidSelections);
                    setShowMultipleSelectionsError(true);
                    setMultipleSelectionsError('Payment should be in Pending or Hold status');
                    moveFocusToContextualError();
                    return;
                }

                if (i > 0) {
                    // If custId mismatch then mark as invalidSelection
                    if (paymentsListData[selectedRow[i - 1]].customerName !== paymentsListData[selectedRow[i]].customerName) {
                        console.log('Customer Name must be same');

                        invalidSelections = true;
                        console.log('Invalid selections is now true', invalidSelections);
                    }

                    if (paymentsListData[selectedRow[i - 1]].instructedCurrency !== paymentsListData[selectedRow[i]].instructedCurrency) {
                        console.log('Instructed Currency must be same');
                        invalidSelections = true;
                        console.log('Invalid selections is now true', invalidSelections);
                    }

                    if (paymentsListData[selectedRow[i - 1]].transactionType !== paymentsListData[selectedRow[i]].transactionType) {
                        console.log('Transaction type must be same');

                        invalidSelections = true;
                        console.log('Invalid selections is now true', invalidSelections);
                    }
                }
                tnxArray.push(paymentsListData[rowIndex].tnxId);
                custId = paymentsListData[rowIndex].custId;
            }

            if (invalidSelections === true) {
                console.log('Show Contextual Error');
                setShowMultipleSelectionsError(true);
                setMultipleSelectionsError(t('userTable.paymentCannotBeProcessed'));
                moveFocusToContextualError();
                return;
            }

            if (tnxArray.length > 0) {
                validateSelections(tnxArray, custId);
            }
        } else {
            setShowMultipleSelectionsError(true);
            setMultipleSelectionsError(t('userTable.pleaseSelectPayment'));
            moveFocusToContextualError();
        }
    };

    const moveFocusToContextualError = () => {
        let elementsWithError = document.getElementsByClassName('payment-selection-error');
        setTimeout(() => {
            console.log('Logging Contextual Notification component which shows error', elementsWithError);
            if (elementsWithError !== null && elementsWithError !== undefined && elementsWithError.length > 0) {
                console.log('Logging Contextual Notification component which shows error before moving focus', elementsWithError[0]);
                elementsWithError[0].focus();
            }
        }, 400);
    };

    const triggerCancellationModal = () => {
        if (cancellationReason === null || cancellationReason === '' || cancellationReason === false) {
            setShowCancellationReasonError(true);
            setCancellationReasonError(t('userTable.cancReasonCannotBeEmpty'));
            return;
        } else {
            setShowCancellationReasonError(false);
            setCancellationReasonError('');
        }
        setOpen(true);
    };

    const cancelPayments = () => {
        const cancelPaymentsCall = async () => {
            try {
                let tnxIds = [];
                for (let i = 0; i < selectedRow.length; i++) {
                    tnxIds.push(paymentsListData[selectedRow[i]].tnxId);
                }
                const response = await axiosInstance.post(
                    CANCEL_PAYMENT_URL,
                    JSON.stringify({
                        cancellReason: cancellationReason,
                        tnxId: tnxIds,
                    })
                );
                setOpen(false);
                if (response.status === 200) {
                    setFetchCallCount((prev) => prev + 1);
                    updateRow([]);
                    setCancellationReason(false);
                }
            } catch (error) {
                setShowMultipleSelectionsError(true);
                setMultipleSelectionsError(error.response.data.message);
                setOpen(false);
                console.log(error);
            }
        };
        cancelPaymentsCall();
    };

    var isSafari = window.safari !== undefined;

    return (
        <StyledGrid container role='main' style={{ paddingBottom: `${footerHeight + 20}px` }}>
            <CustomerLogo />
            <Grid item xs={12}>
                <Box>
                    <Box borderLeft='3px solid red' paddingLeft='10px'>
                        <Typography variant='h1' marginBottom={4} id='paymentsList-sectionTitle' className='page-title' tabIndex='-1'>
                            {t('userTable.section_title')}
                        </Typography>
                    </Box>
                </Box>
            </Grid>
            {isRegisteredUser ? (
                <Grid item xs={12}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm='auto'>
                            <Box marginBottom={['10px', '10px', '10px', '10px']}>
                                <Button
                                    id='button-payment-history'
                                    variant='light.tertiary'
                                    onClick={() => {
                                        history.push(PAYMENT_HISTORY);
                                    }}
                                >
                                    Payment history
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm='auto'>
                            <Box marginBottom={['10px', '10px', '10px', '10px']}>
                                <Button
                                    id='button-account-history'
                                    variant='light.tertiary'
                                    onClick={() => {
                                        history.push(ACCOUNT_HISTORY);
                                    }}
                                >
                                    {t('userTable.accounts')}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            ) : null}

            <Grid item xs={12}>
                <Box marginBottom={2}>
                    <Typography variant='body1' marginBottom={4}>
                        {t('userTable.select_payment')}
                    </Typography>
                </Box>
            </Grid>
            <Grid item xs={12}>
                {showMultipleSelectionsError ? (
                    <ContextualNotification
                        tabIndex='-1'
                        className='payment-selection-error'
                        marginBottom={['10px', '10px', '10px', '24px']}
                        title=''
                        variant='error'
                        iconAriaLabel='error'
                        onClose={() => {
                            setShowMultipleSelectionsError(false);
                        }}
                    >
                        {multipleSelectionsError}
                    </ContextualNotification>
                ) : null}
            </Grid>
            <GridWithScroll container>
                <Grid item xs={12}>
                    <Table {...getTableProps()} tableHover width='99%' id='pl-table-paymentsList'>
                        <TableHead>
                            {headerGroups.map((headerGroup) => (
                                <TableHeadRow
                                    {...headerGroup.getHeaderGroupProps()}
                                    onClick={() => {
                                        updateRow([]);
                                    }}
                                >
                                    {showCheckboxes ? <TableHeadCell>Select</TableHeadCell> : null}

                                    {headerGroup.headers.map((column) => {
                                        return (
                                            <TableHeadCell
                                                cellType='sortable'
                                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                                isSorted={column.isSorted}
                                                aria-sort={column.canSort ? (column.isSortedDesc ? 'descending' : 'ascending') : undefined}
                                                onKeyPress={(event) => {
                                                    if (event.charCode === 13) {
                                                        column.toggleSortBy();
                                                    }
                                                }}
                                            >
                                                {column.render('Header')}
                                                <TSortLabel
                                                    aria-label={'Sort ' + `${column.Header}`}
                                                    sortType={column.canSort ? (column.isSortedDesc ? 'descending' : 'ascending') : undefined}
                                                />
                                            </TableHeadCell>
                                        );
                                    })}
                                </TableHeadRow>
                            ))}
                        </TableHead>
                        <TableBody {...getTableBodyProps()}>
                            {rows.map((row, i) => {
                                // Ignore this warning or just return something to suppress it
                                if (
                                    // This "if" ensures that records are displayed according to 'Page Size' and 'Selected Page'
                                    pageIndex * pageSize <= i &&
                                    i < (pageIndex + 1) * pageSize
                                ) {
                                    prepareRow(row);

                                    return (
                                        <TableBodyRow
                                            style={{ cursor: cursor }}
                                            onMouseEnter={() => {
                                                setCursor('pointer');
                                            }}
                                            onMouseLeave={() => {
                                                setCursor('default');
                                            }}
                                            selected={selectedRow.indexOf(row.id) !== -1}
                                            aria-selected={selectedRow.indexOf(row.id) !== -1 ? true : false}
                                            {...row.getRowProps()}
                                        >
                                            {showCheckboxes ? (
                                                <TableBodyCell
                                                    id={`row${row.id}colX`}
                                                    key={`row${row.id}colX`}
                                                    style={{ padding: '1em 0.5em 0.5em 1em' }}
                                                >
                                                    <NativeCheckbox
                                                        variant='light.small'
                                                        aria-labelledby={`row${row.id}col3`}
                                                        background='white'
                                                        id={`${row.id}Checkbox`}
                                                        checked={selectedRow.indexOf(row.id) !== -1}
                                                        onChange={() => handleClick(row.id)}
                                                    />
                                                </TableBodyCell>
                                            ) : null}

                                            {row.cells.map((cell, index) => {
                                                return (
                                                    <TableBodyCell
                                                        style={{ padding: '0.5em 1em 0.5em 1em', height: '-webkit-fill-available' }}
                                                        onClick={() => {
                                                            console.log('Logging Cell', cell.row.original);
                                                            handleSingleRowClick(cell);
                                                        }}
                                                        {...cell.getCellProps()}
                                                        id={`row${row.id}col${index}`}
                                                        textAlign={typeof cell.value === 'number' ? 'right' : 'left'}
                                                    >
                                                        {cell.render('Cell')}
                                                    </TableBodyCell>
                                                );
                                            })}
                                        </TableBodyRow>
                                    );
                                }
                            })}
                        </TableBody>
                    </Table>
                </Grid>
            </GridWithScroll>
            {numberOfRecords > 10 ? (
                <Pagination
                    count={numberOfRecords}
                    defaultCountPerPage={10}
                    defaultCurrentPage={1}
                    width='100%'
                    display='flex'
                    flexWrap='wrap'
                    marginBottom='1em'
                >
                    <Box marginTop='1em'>
                        <AmountSelector
                            id='pl-dropdown-pageSize'
                            amountComponent={AmountComponent}
                            options={amountOptions}
                            onAmountChange={(countPerPage: number) => {
                                setPageSize(countPerPage);
                                gotoPage(0);
                                updateRow([]);
                            }}
                        />
                    </Box>

                    <Box display='flex' marginTop='1em'>
                        <PageSelector
                            id='pl-dropdown-pageSelector'
                            isSearchable={true}
                            onPageChange={(currentPage, event, { countPerPage }) => {
                                gotoPage(currentPage - 1);
                                updateRow([]);
                            }}
                        />
                        <NextPreviousBtn
                            id='pl-buttons-nextPrevious'
                            previousOnClick={(newCurrentPage, count, { countPerPage }) => {
                                previousPage();
                                updateRow([]);
                            }}
                            nextOnClick={(newCurrentPage, count, { countPerPage }) => {
                                nextPage();
                                updateRow([]);
                            }}
                        />
                    </Box>
                </Pagination>
            ) : null}

            {showPaymentCancellation ? (
                <>
                    <Grid container>
                        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}></Grid>
                        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                            <Box display='flex' flexDirection='row' marginTop={numberOfRecords < 10 ? '1em' : '0em'}>
                                <TypographyReason variant='body1' marginTop='0.5em' marginRight='1em'>
                                    {t('userTable.reason')}*
                                </TypographyReason>

                                <TextInput
                                    id='paymentsList-textbox-cancelReason'
                                    variant='light.large'
                                    placeholder={t('userTable.cancelPaymentPlaceholder')}
                                    onChange={(event) => {
                                        if (event.value !== '' || event.value !== null) {
                                            setShowCancellationReasonError(false);
                                            setCancellationReasonError(false);
                                        }
                                        setCancellationReason(event.value);
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}></Grid>
                        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                            <Box display='flex' flexDirection='row'>
                                <Typography variant='body1' marginTop='0.5em' marginRight='1em' style={{ visibility: 'hidden' }}>
                                    {t('userTable.reason')}*
                                </Typography>
                                {showCancellationReasonError ? (
                                    <InlineNotificationComponent
                                        id='paymentsList-inlineError-cancelReason'
                                        title=''
                                        content={cancellationReasonError}
                                    />
                                ) : null}
                            </Box>
                        </Grid>
                    </Grid>
                </>
            ) : null}

            <Grid container>
                <Box
                    display='flex'
                    flexDirection='row'
                    justifyContent='flex-end'
                    marginTop={4}
                    paddingTop={4}
                    marginBottom={['30px', '30px', '30px', '100px']}
                    width='100%'
                    borderTop='1px solid #333'
                >
                    <Box>
                        <Button
                            disabled={!showPaymentCancellation}
                            id='paymentsList-button-cancelPayment'
                            variant='light.tertiary'
                            onClick={() => {
                                triggerCancellationModal();
                            }}
                        >
                            {t('userTable.cancelPayment')}
                        </Button>
                    </Box>
                    <Box marginLeft='1em'>
                        <Button
                            id='pl-button-continue'
                            onClick={() => handleMultipleRowSelect()}
                            variant='light.primary'
                            disabled={showPaymentCancellation}
                        >
                            {t('userTable.continue_button')}
                        </Button>
                    </Box>
                </Box>
            </Grid>
            <Modal
                open={open}
                onHide={() => setOpen(false)}
                closeIcon={true}
                onClickCloseIcon={() => setOpen(false)}
                size='small'
                activeFocusTrap={true}
                aria-labelledby='title1'
                minHeight='230px'
            >
                <Box display='flex' justifyContent='center' width='100%' marginTop='10%'>
                    <Box>
                        <Typography variant='h4' id='paymentsList-typo-modal-text' fontWeight='bold'>
                            {t('userTable.doYouWantToCancel')}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    position='absolute'
                    bottom='0'
                    py='1em'
                    width='calc(100% - 80px)'
                    display='flex'
                    justifyContent='space-between'
                    borderTop='1px solid #333'
                >
                    <Button variant='light.tertiary' onClick={() => setOpen(false)} id='paymentsList-button-modal-no'>
                        {t('userTable.no')}
                    </Button>
                    <Button
                        onClick={() => {
                            cancelPayments();
                        }}
                        id='paymentsList-button-modal-yes'
                    >
                        {t('userTable.yes')}
                    </Button>
                </Box>
            </Modal>
        </StyledGrid>
    );
};

export default PaymentsListTable;
